import { Typography } from '@jouzen/ecom-components/v3/Typography';
import { cx } from 'class-variance-authority';
import NextLink from 'next/link';
import type {
  HTMLAttributeAnchorTarget,
  PropsWithChildren,
  ReactNode,
} from 'react';

import { LEGAL_FOOTNOTES_ID } from '@/app/consts/ids';
import logToDatadog from '@/logger/logToDatadog';

export type Tag =
  | 'break'
  | 'breakSm'
  | 'breakLg'
  | 'wbreak'
  | 'strong'
  | 'b'
  | 'em'
  | 'i'
  | 'u'
  | 'sup'
  | 'li'
  | 'sr'
  | 'p'
  | 'w'
  | 'uppercase'
  | 'Link'
  | 'ExternalLink'
  | 'EmailLink'
  | 'HomeLink'
  | 'TermsLink'
  | 'IpNoticeLink'
  | 'FootnoteLink'
  | 'ScienceResearchLink'
  | 'PrivacyPolicyLink';

export interface LocaleStringTag {
  r: string;
  tm: string;
}

export const getLocaleFormatString: LocaleStringTag = {
  // In Phrase, these tokens are simple text replacements for values wrapped in curly braces like {tm}
  r: String.fromCharCode(174),
  tm: String.fromCharCode(8482),
};

const hrefFromChunks = (chunks: unknown): string => {
  if (typeof chunks === 'string') {
    return chunks;
  } else if (Array.isArray(chunks)) {
    return chunks.map((chunk) => hrefFromChunks(chunk)).join('');
  } else {
    logToDatadog({
      data: { chunks },
      message: `Unexpected chunk type in locale formatting link: ${typeof chunks}`,
      module: 'i18n',
    });
    return chunks as string;
  }
};

interface LocaleFormattingLinkProps {
  readonly className?: string;
  readonly href: string;
  readonly isExternal?: boolean;
  readonly rel?: string;
  readonly target?: HTMLAttributeAnchorTarget;
}

export const LocaleFormattingLink = ({
  children,
  className,
  href,
  isExternal = false,
  ...props
}: PropsWithChildren<LocaleFormattingLinkProps>) => {
  const LinkEl = !isExternal ? NextLink : 'a';
  return (
    <LinkEl
      className={cx('text-sandstone-500 underline', className)}
      href={href}
      {...props}
    >
      {children}
    </LinkEl>
  );
};

const getLocaleFormatting: Record<Tag, (chunks: ReactNode) => ReactNode> = {
  // Simple HTML tags (i.e. <b>bold text</b>)
  break: function breakTag() {
    return <br />;
  },
  breakSm: function breakSm() {
    return <br className="block md:hidden" />;
  },
  breakLg: function breakLg() {
    return <br className="hidden lg:block" />;
  },
  wbreak: function wbreakTag() {
    return <wbr />;
  },
  strong: function strong(chunks: ReactNode) {
    return <strong>{chunks}</strong>;
  },
  b: function b(chunks: ReactNode) {
    return <strong>{chunks}</strong>;
  },
  em: function em(chunks: ReactNode) {
    return <em className="font-serif italic">{chunks}</em>;
  },
  i: function i(chunks: ReactNode) {
    return <em>{chunks}</em>;
  },
  u: function u(chunks: ReactNode) {
    return <span className="underline">{chunks}</span>;
  },
  sup: function sup(chunks: ReactNode) {
    return <sup>{chunks}</sup>;
  },
  li: function li(chunks: ReactNode) {
    return <li>{chunks}</li>;
  },
  sr: function sr(chunks: ReactNode) {
    return <span className="sr-only">{chunks}</span>;
  },
  p: function p(chunks: ReactNode) {
    return <Typography color="inherit">{chunks}</Typography>;
  },
  // Japanese-specific formatting for {w} characters
  w: function w(chunks: ReactNode) {
    return <>{chunks}</>;
  },
  uppercase: function uppercase(chunks: ReactNode) {
    return <span className="uppercase">{chunks}</span>;
  },

  // React Components & more complex HTML tag usage
  Link: function Link(chunks: ReactNode) {
    return (
      <LocaleFormattingLink href={hrefFromChunks(chunks)}>
        {chunks}
      </LocaleFormattingLink>
    );
  },

  ExternalLink: function ExternalLink(chunks: ReactNode) {
    return (
      <LocaleFormattingLink
        href={hrefFromChunks(chunks)}
        isExternal
        rel="noreferrer"
        target="_blank"
      >
        {chunks}
      </LocaleFormattingLink>
    );
  },

  EmailLink: function EmailLink(chunks: ReactNode) {
    return (
      <LocaleFormattingLink
        href={`mailto:${hrefFromChunks(chunks)}`}
        isExternal
      >
        {chunks}
      </LocaleFormattingLink>
    );
  },

  HomeLink: function HomeLink(chunks: ReactNode) {
    return (
      <LocaleFormattingLink className="text-inherit!" href="/">
        {chunks}
      </LocaleFormattingLink>
    );
  },

  TermsLink: function TermsLink(chunks: ReactNode) {
    return (
      <LocaleFormattingLink href="/terms-and-conditions">
        {chunks}
      </LocaleFormattingLink>
    );
  },

  IpNoticeLink: function IpNoticeLink(chunks: ReactNode) {
    return (
      <LocaleFormattingLink href="/intellectual-property-notice">
        {chunks}
      </LocaleFormattingLink>
    );
  },

  FootnoteLink: (chunks: ReactNode) => {
    return (
      <sup>
        <a href={`#${LEGAL_FOOTNOTES_ID}`}>{chunks}</a>
      </sup>
    );
  },

  ScienceResearchLink: function ScienceResearchLink(chunks: ReactNode) {
    return (
      <LocaleFormattingLink href="/science-and-research">
        {chunks}
      </LocaleFormattingLink>
    );
  },

  PrivacyPolicyLink: function PrivacyPolicyLink(chunks: ReactNode) {
    return (
      <LocaleFormattingLink href="/privacy-policy">
        {chunks}
      </LocaleFormattingLink>
    );
  },
};

export default getLocaleFormatting;
